import { useI18n } from "../i18n/I18nContext";
import { useState } from "react";
import { Play, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, FeatureCard, CtaBlock } from "../components/Shared";

const HERO_IMG =
  "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?auto=format&fit=crop&w=1920&q=80";

export default function Filming() {
  const { t, lang, content } = useI18n();
  const [activeVideo, setActiveVideo] = useState(null);
  const fallbackLang = lang === "ru" ? "en" : "ru";
  const primaryCases = content?.[lang]?.filming?.cases || t.filming.cases || [];
  const fallbackCases = content?.[fallbackLang]?.filming?.cases || [];

  const mergedByIndex =
    primaryCases.length > 0
      ? primaryCases.map((item, idx) => {
          const fb = fallbackCases[idx] || {};
          return {
            title: item?.title || fb.title || "",
            youtubeUrl: item?.youtubeUrl || fb.youtubeUrl || "",
          };
        })
      : fallbackCases;

  const normalizedCases = mergedByIndex
    .map((item) => ({
      title: (item?.title || "").trim(),
      youtubeUrl: (item?.youtubeUrl || "").trim(),
    }))
    .filter((item) => item.title || item.youtubeUrl);

  const dedupedCases = normalizedCases.filter((item, idx, arr) => {
    const key = `${item.title.toLowerCase()}|${item.youtubeUrl.toLowerCase()}`;
    return arr.findIndex((x) => `${x.title.toLowerCase()}|${x.youtubeUrl.toLowerCase()}` === key) === idx;
  });

  const cases = dedupedCases.length > 0 ? dedupedCases : [
    { title: "RTVI", youtubeUrl: "" },
    { title: "808", youtubeUrl: "" },
    { title: "Pavel Gitelman", youtubeUrl: "" },
    { title: "Production studios", youtubeUrl: "" },
  ];

  const toEmbed = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace("www.", "");
      if (host === "youtu.be") {
        const id = parsed.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : "";
      }
      if (host === "youtube.com" || host === "m.youtube.com") {
        if (parsed.pathname === "/watch") {
          const id = parsed.searchParams.get("v");
          return id ? `https://www.youtube.com/embed/${id}` : "";
        }
        if (parsed.pathname.startsWith("/embed/")) return `https://www.youtube.com${parsed.pathname}`;
        if (parsed.pathname.startsWith("/shorts/")) {
          const id = parsed.pathname.split("/")[2];
          return id ? `https://www.youtube.com/embed/${id}` : "";
        }
      }
    } catch {
      return "";
    }
    return "";
  };

  const getYoutubeThumb = (url) => {
    const embed = toEmbed(url);
    if (!embed) return "";
    const id = embed.split("/embed/")[1]?.split("?")[0];
    return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : "";
  };

  return (
    <>
      <PageHero
        overline={t.filming.overline}
        title={t.filming.title}
        desc={t.filming.desc}
        image={t.filming.heroImage || HERO_IMG}
      />

      <section className="bg-midnight border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="overline mb-6">SERVICES · 服务</div>
            <h2 className="font-display font-extrabold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] max-w-3xl uppercase">
              Полный продакшн-сервис
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {t.filming.blocks.map((b, idx) => (
              <StaggerItem key={idx} className="bg-midnight-2">
                <FeatureCard title={b.t} desc={b.d} index={idx + 1} />
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-[#0E1117]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="overline mb-6">CLIENTS · 客户</div>
            <h2 className="font-display font-extrabold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] max-w-3xl uppercase">
              Среди наших съёмочных проектов
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((item, idx) => (
              <div
                key={`${item.title}-${idx}`}
                className="bg-[#0E1117] border border-white/10 p-6 md:p-8 h-full flex flex-col gap-4"
              >
                <div className="font-display font-extrabold text-white text-xl md:text-2xl tracking-tight uppercase min-h-[72px] flex items-start">
                  {item.title}
                </div>
                <div
                  className="aspect-video bg-midnight-2 border border-white/10 overflow-hidden mt-auto relative group cursor-pointer"
                  onClick={() => {
                    const embed = toEmbed(item.youtubeUrl);
                    if (embed) setActiveVideo({ title: item.title, embed });
                  }}
                >
                  {toEmbed(item.youtubeUrl) ? (
                    <>
                      <img
                        src={getYoutubeThumb(item.youtubeUrl)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-midnight/35 group-hover:bg-midnight/20 transition-colors" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-coral/95 flex items-center justify-center group-hover:scale-105 transition-transform">
                          <Play size={18} className="text-midnight ml-0.5 fill-midnight" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/40 text-sm">
                      YouTube link not set
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm p-4 md:p-10"
            onClick={() => setActiveVideo(null)}
          >
            <div className="mx-auto h-full max-w-6xl flex items-center">
              <div
                className="relative w-full bg-black border border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setActiveVideo(null)}
                  className="absolute -top-11 right-0 text-white/80 hover:text-white p-2"
                  aria-label="Close video"
                >
                  <X size={22} />
                </button>
                <div className="aspect-video bg-black">
                  <iframe
                    src={activeVideo.embed}
                    title={activeVideo.title || "Video"}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaBlock title={t.filming.cta} buttonText={t.nav.cta} />
    </>
  );
}
